import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, ChevronRight, Plus, Search, Users, X } from 'lucide-react';
import { fieldClass, ghostBtn, iconBtn, labelClass } from '../components/dataModelsUi';
import { logDataModels } from '../dataModelsLogger';
import ConditionBuilder, { emptyGroup, GroupActions, TreeChildren } from './ConditionBuilder';
import ExpressionEditor from './ExpressionEditor';
import { defaultRule, emptySubject, ruleDefinitionMode } from './engine';
import type { PolicyDirectoryOption } from './policyDirectory';
import type { QueryTestResult } from './queryTest';
import { assetFieldLabel, assetsForRowFilter, usePolicyAssetColumns } from './usePolicyAssetColumns';
import { usePolicyDirectory } from './usePolicyDirectory';
import type {
  AccessOperation,
  Classification,
  GovernancePolicy,
  MaskAction,
  PolicyAsset,
  PolicyDefinitionMode,
  PolicyRule,
  PolicyType,
  SubjectSelector,
} from './types';
import { EXAMPLE_EXPRESSIONS } from './types';

export interface ExpressionWorkbenchProps {
  testUserId: string;
  onTestUserId: (id: string) => void;
  testResult: QueryTestResult | null;
  onRunTest: () => void;
  onCopyNotice: (message: string) => void;
  onOpenHelp?: () => void;
}

const CLASSIFICATIONS: Classification[] = ['Public', 'Internal', 'Confidential', 'Restricted', 'PII', 'PHI', 'PCI'];
const OPERATIONS: AccessOperation[] = ['SELECT', 'INSERT', 'UPDATE', 'DELETE', 'EXECUTE'];
const WEEKDAYS = [
  { id: 1, label: 'Mon' },
  { id: 2, label: 'Tue' },
  { id: 3, label: 'Wed' },
  { id: 4, label: 'Thu' },
  { id: 5, label: 'Fri' },
  { id: 6, label: 'Sat' },
  { id: 0, label: 'Sun' },
];

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold">{title}</h3>
      {children}
    </section>
  );
}

export function TargetPicker({
  policy,
  onChange,
  assets,
  isLight,
  lockCurrent,
}: {
  policy: GovernancePolicy;
  onChange: (policy: GovernancePolicy) => void;
  assets: PolicyAsset[];
  isLight: boolean;
  lockCurrent?: boolean;
}) {
  return (
    <div className="space-y-3">
      {!lockCurrent ? (
        <div className="flex flex-wrap gap-2">
          {(['all', 'selected', 'dynamic'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              className={policy.target.mode === mode ? ghostBtn(isLight) + ' ring-1 ring-[#1B6CA8]' : ghostBtn(isLight)}
              onClick={() => onChange({ ...policy, target: { ...policy.target, mode } })}
            >
              {mode === 'all' ? 'All assets in scope' : mode === 'selected' ? 'Selected assets' : 'Dynamic match'}
            </button>
          ))}
        </div>
      ) : (
        <p className={`text-xs ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>This asset is the policy owner. Target selection stays on {assets[0]?.name ?? 'the current asset'}.</p>
      )}
      {policy.target.mode === 'selected' || lockCurrent ? (
        <AssetMultiSelect
          assets={assets}
          selectedIds={lockCurrent ? assets.map((asset) => asset.id) : policy.target.assetIds}
          onChange={(assetIds) => onChange({ ...policy, target: { ...policy.target, assetIds, mode: 'selected' } })}
          isLight={isLight}
          disabled={lockCurrent}
        />
      ) : null}
      {policy.target.mode === 'dynamic' ? (
        <>
          <DynamicMatchFields policy={policy} onChange={onChange} isLight={isLight} />
          <p className={`text-xs ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>Newly discovered columns that match this rule are covered automatically. Unclassified columns are listed in impact preview.</p>
        </>
      ) : null}
    </div>
  );
}

function AssetMultiSelect({
  assets,
  selectedIds,
  onChange,
  isLight,
  disabled,
}: {
  assets: PolicyAsset[];
  selectedIds: string[];
  onChange: (assetIds: string[]) => void;
  isLight: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const selected = useMemo(() => assets.filter((asset) => selectedIds.includes(asset.id)), [assets, selectedIds]);
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return assets.filter((asset) => {
      if (!needle) return true;
      return `${asset.name} ${asset.classification ?? ''} ${asset.tags.join(' ')}`.toLowerCase().includes(needle);
    });
  }, [assets, query]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  const toggle = (id: string) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((item) => item !== id) : [...selectedIds, id]);
  };

  const summary = selected.length
    ? selected.length === 1
      ? selected[0].name
      : `${selected.length} assets selected`
    : 'Select assets';

  return (
    <div ref={root} className="relative">
      <label className={labelClass(isLight)}>Assets</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={`${fieldClass(isLight)} flex items-center justify-between gap-2 text-left ${disabled ? 'opacity-70' : ''}`}
      >
        <span className={selected.length ? '' : isLight ? 'text-[#9AA1AB]' : 'text-slate-500'}>{summary}</span>
        <ChevronDown size={14} className={isLight ? 'text-[#6B7280]' : 'text-slate-400'} />
      </button>
      {selected.length ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selected.map((asset) => (
            <span key={asset.id} className={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-mono text-[11px] ${isLight ? 'bg-[#F2F4F7] text-[#3C4048]' : 'bg-white/10 text-slate-200'}`}>
              {asset.name}
              {!disabled ? (
                <button type="button" className={`${iconBtn(isLight)} !h-5 !w-5`} aria-label={`Remove ${asset.name}`} onClick={() => toggle(asset.id)}>
                  <X size={11} />
                </button>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}
      {open && !disabled ? (
        <div className={`absolute z-30 mt-1 w-full overflow-hidden rounded-md border shadow-md ${isLight ? 'border-[#E6E8EB] bg-white shadow-slate-200/80' : 'border-white/10 bg-black'}`}>
          <div className="relative border-b px-2 py-2">
            <Search size={13} className={`absolute left-4 top-1/2 -translate-y-1/2 ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`} />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search assets"
              className={isLight
                ? 'h-8 w-full rounded-md border border-[#D8DCE2] bg-white py-0 pl-8 pr-3 text-sm text-[#1C1C1C] placeholder:text-[#9AA1AB] focus:border-[#1B6CA8] focus:outline-none'
                : 'h-8 w-full rounded-md border border-white/15 bg-black py-0 pl-8 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-white/30 focus:outline-none'}
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {visible.map((asset) => {
              const checked = selectedIds.includes(asset.id);
              return (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => toggle(asset.id)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left ${isLight ? 'hover:bg-[#F3F7FB]' : 'hover:bg-white/5'}`}
                >
                  <span className={`inline-flex h-4 w-4 items-center justify-center rounded border ${checked ? 'border-[#1B6CA8] bg-[#1B6CA8] text-white' : isLight ? 'border-[#D8DCE2]' : 'border-white/20'}`}>
                    {checked ? <Check size={11} /> : null}
                  </span>
                  <span className={`font-mono text-xs ${isLight ? 'text-[#1C1C1C]' : 'text-slate-100'}`}>{asset.name}</span>
                  <span className={`ml-auto text-[11px] ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>{asset.classification ?? 'Unclassified'}</span>
                </button>
              );
            })}
            {!visible.length ? <p className={`px-3 py-3 text-xs ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>No assets match this search.</p> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function DynamicMatchFields({ policy, onChange, isLight }: { policy: GovernancePolicy; onChange: (policy: GovernancePolicy) => void; isLight: boolean }) {
  const match = policy.target.match;
  const toggle = (key: 'classifications' | 'tags' | 'dataTypes', value: string) => {
    const current = match[key] as string[];
    const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
    onChange({ ...policy, target: { ...policy.target, match: { ...match, [key]: next } } });
  };
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <div>
        <label className={labelClass(isLight)}>Classifications</label>
        <div className="flex flex-wrap gap-1.5">
          {CLASSIFICATIONS.map((item) => (
            <button key={item} type="button" className={ghostBtn(isLight)} onClick={() => toggle('classifications', item)}>
              {match.classifications.includes(item) ? `✓ ${item}` : item}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={labelClass(isLight)}>Tags</label>
        <input className={fieldClass(isLight)} value={match.tags.join(', ')} onChange={(event) => onChange({ ...policy, target: { ...policy.target, match: { ...match, tags: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) } } })} placeholder="pii, payroll" />
      </div>
      <div>
        <label className={labelClass(isLight)}>Column name</label>
        <input className={fieldClass(isLight)} value={match.columnName} onChange={(event) => onChange({ ...policy, target: { ...policy.target, match: { ...match, columnName: event.target.value } } })} placeholder="email" />
      </div>
      <div>
        <label className={labelClass(isLight)}>Column pattern</label>
        <input className={fieldClass(isLight)} value={match.columnPattern} onChange={(event) => onChange({ ...policy, target: { ...policy.target, match: { ...match, columnPattern: event.target.value } } })} placeholder="*phone*" />
      </div>
      <div>
        <label className={labelClass(isLight)}>Data types</label>
        <input className={fieldClass(isLight)} value={match.dataTypes.join(', ')} onChange={(event) => onChange({ ...policy, target: { ...policy.target, match: { ...match, dataTypes: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) } } })} placeholder="VARCHAR, DECIMAL" />
      </div>
      <div>
        <label className={labelClass(isLight)}>Combine</label>
        <select className={fieldClass(isLight)} value={match.combinator} onChange={(event) => onChange({ ...policy, target: { ...policy.target, match: { ...match, combinator: event.target.value as 'AND' | 'OR' } } })}>
          <option value="AND">AND</option>
          <option value="OR">OR</option>
        </select>
      </div>
    </div>
  );
}

const OPERATION_LABEL: Record<AccessOperation, string> = {
  SELECT: 'Read',
  INSERT: 'Write',
  UPDATE: 'Update',
  DELETE: 'Delete',
  EXECUTE: 'Execute',
};

function subjectLabel(subject: SubjectSelector, users: PolicyDirectoryOption[] = []): string {
  if (subject.mode === 'everyone') return 'everyone';
  if (subject.mode === 'users') {
    const names = subject.userIds.map((id) => users.find((user) => user.id === id)?.label ?? id);
    return names.length ? names.join(', ') : 'selected users';
  }
  if (subject.mode === 'roles') return subject.roles.length ? subject.roles.join(', ') : 'selected roles';
  if (subject.mode === 'groups') return subject.groups.length ? subject.groups.join(', ') : 'selected groups';
  if (subject.mode === 'attributes') {
    const op = subject.attribute.operator === 'neq' ? 'is not' : subject.attribute.operator === 'contains' ? 'contains' : 'is';
    return `users whose ${subject.attribute.key} ${op} ${subject.attribute.value || '…'}`;
  }
  return subject.expression.trim() || 'users matching an expression';
}

function subjectTypeLabel(subject: SubjectSelector): string {
  if (subject.mode === 'everyone') return 'Everyone';
  if (subject.mode === 'users') return 'Specific people';
  if (subject.mode === 'roles') return 'People with a role';
  if (subject.mode === 'groups') return 'People in a group';
  if (subject.mode === 'attributes') return 'People matching an attribute';
  return 'People matching an expression';
}

function summarizeRule(rule: PolicyRule, users: PolicyDirectoryOption[] = []): string {
  const who = subjectLabel(rule.subject, users);
  if (rule.action.type === 'ACCESS') {
    const ops = rule.action.operations.map((operation) => OPERATION_LABEL[operation]).join(', ') || 'no actions';
    return rule.action.effect === 'deny' ? `${who} cannot ${ops}` : `${who} can ${ops}`;
  }
  if (rule.action.type === 'COLUMN' || rule.action.type === 'SENSITIVE') {
    return `${who}: ${maskLabel(rule.action.mask)}`;
  }
  if (rule.action.type === 'TIME') {
    if (rule.action.effect === 'expire') return `${who}: access expires ${rule.action.window.end ? new Date(rule.action.window.end).toLocaleDateString() : 'after a date'}`;
    if (rule.action.effect === 'deny_window') return `${who}: denied during the time window`;
    return `${who}: allowed only during the time window`;
  }
  return `Applies to ${who}`;
}

function maskLabel(mask: MaskAction): string {
  const labels: Record<MaskAction['kind'], string> = {
    full_null: 'replace values with NULL',
    full_fixed: 'replace values with a fixed value',
    full_string: 'fully mask values',
    partial_first: 'show only the first characters',
    partial_last: 'show only the last characters',
    partial_email: 'mask email addresses',
    partial_phone: 'mask phone numbers',
    partial_custom: 'apply a custom mask',
    deny_column: 'block the column',
    deny_asset: 'block the asset',
    custom_hash: 'hash values',
    custom_expression: 'apply a custom mask expression',
    none: 'no extra mask',
  };
  return labels[mask.kind];
}

export function SubjectFields({
  subject,
  onChange,
  isLight,
  assets,
  layout = 'stack',
  label = 'Who',
  everyoneLabel = 'Everyone',
}: {
  subject: SubjectSelector;
  onChange: (subject: SubjectSelector) => void;
  isLight: boolean;
  assets: PolicyAsset[];
  layout?: 'stack' | 'row';
  label?: string;
  everyoneLabel?: string;
}) {
  const directory = usePolicyDirectory();
  const inline = layout === 'row';
  const compactField = `${fieldClass(isLight)} !h-8 !rounded-none !py-0 !text-[13px]`;
  const modeSelect = (
    <select aria-label={label} className={inline ? compactField : fieldClass(isLight)} value={subject.mode} onChange={(event) => onChange({ ...subject, mode: event.target.value as SubjectSelector['mode'] })}>
      <option value="everyone">{everyoneLabel}</option>
      <option value="users">Specific people</option>
      <option value="roles">People with a role</option>
      <option value="groups">People in a group</option>
      <option value="attributes">People matching an attribute</option>
      <option value="expression">People matching an expression</option>
    </select>
  );
  const peoplePicker = subject.mode === 'users' ? (
    <OptionMultiSelect
      label="People"
      placeholder="Select people"
      options={directory.users}
      selectedIds={subject.userIds}
      onChange={(userIds) => onChange({ ...subject, userIds })}
      isLight={isLight}
      loading={directory.loading}
      emptyText={directory.error || 'No people available'}
      hideLabel={inline}
      compact={inline}
    />
  ) : null;
  const rolePicker = subject.mode === 'roles' ? (
    <OptionMultiSelect
      label="Roles"
      placeholder="Select roles"
      options={directory.roles}
      selectedIds={subject.roles}
      onChange={(roles) => onChange({ ...subject, roles })}
      isLight={isLight}
      loading={directory.loading}
      emptyText={directory.error || 'No roles available'}
      hideLabel={inline}
      compact={inline}
    />
  ) : null;
  const groupPicker = subject.mode === 'groups' ? (
    <OptionMultiSelect
      label="Groups"
      placeholder="Select groups"
      options={directory.groups}
      selectedIds={subject.groups}
      onChange={(groups) => onChange({ ...subject, groups })}
      isLight={isLight}
      loading={directory.loading}
      emptyText={directory.error || 'No groups available'}
      hideLabel={inline}
      compact={inline}
    />
  ) : null;
  const picker = peoplePicker || rolePicker || groupPicker;
  const attributes = subject.mode === 'attributes' ? (
    <div className={`grid gap-2 md:grid-cols-3 ${inline ? 'min-w-[240px] flex-1' : ''}`}>
      <select aria-label="Attribute" className={inline ? compactField : fieldClass(isLight)} value={subject.attribute.key} onChange={(event) => onChange({ ...subject, attribute: { ...subject.attribute, key: event.target.value } })}>
        <option value="department">Department</option>
        <option value="region">Region</option>
        <option value="clearance">Clearance</option>
      </select>
      <select aria-label="Attribute operator" className={inline ? compactField : fieldClass(isLight)} value={subject.attribute.operator} onChange={(event) => onChange({ ...subject, attribute: { ...subject.attribute, operator: event.target.value as SubjectSelector['attribute']['operator'] } })}>
        <option value="eq">is</option>
        <option value="neq">is not</option>
        <option value="contains">contains</option>
      </select>
      <input aria-label="Attribute value" className={inline ? compactField : fieldClass(isLight)} value={subject.attribute.value} onChange={(event) => onChange({ ...subject, attribute: { ...subject.attribute, value: event.target.value } })} placeholder="HR" />
    </div>
  ) : null;
  const expression = subject.mode === 'expression' ? (
    <ExpressionEditor value={subject.expression} onChange={(expression) => onChange({ ...subject, expression })} assets={assets} isLight={isLight} />
  ) : null;

  if (inline) {
    return (
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`${labelClass(isLight)} !mb-0 shrink-0`}>{label}</span>
          <div className="w-44">{modeSelect}</div>
          {picker}
          {attributes}
        </div>
        {expression}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <label className={labelClass(isLight)}>{label}</label>
        {modeSelect}
      </div>
      {picker}
      {attributes}
      {expression}
    </div>
  );
}

function OptionMultiSelect({
  label,
  placeholder,
  options,
  selectedIds,
  onChange,
  isLight,
  loading = false,
  emptyText = 'No options available',
  hideLabel = false,
  compact = false,
}: {
  label: string;
  placeholder: string;
  options: Array<{ id: string; label: string; detail?: string }>;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  isLight: boolean;
  loading?: boolean;
  emptyText?: string;
  hideLabel?: boolean;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [reviewQuery, setReviewQuery] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const optionById = new Map(options.map((option) => [option.id, option]));
  const selected = selectedIds.map((id) => optionById.get(id) ?? { id, label: id });
  const orphans = selected.filter((option) => !optionById.has(option.id));
  const visible = [...orphans, ...options].filter(
    (option, index, list) =>
      list.findIndex((item) => item.id === option.id) === index
      && `${option.label} ${option.detail ?? ''}`.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const reviewed = selected.filter((option) => `${option.label} ${option.detail ?? ''}`.toLowerCase().includes(reviewQuery.trim().toLowerCase()));
  const buttonLabel = loading
    ? `Loading ${label.toLowerCase()}…`
    : selected.length
      ? selected.map((option) => option.label).join(', ')
      : placeholder;
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const panel = isLight ? 'border-[#E6E8EB] bg-white shadow-slate-200/80' : 'border-white/10 bg-black';

  useEffect(() => {
    if (!open && !reviewOpen) return;
    const onPointer = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) {
        setOpen(false);
        setReviewOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open, reviewOpen]);

  const openReview = () => {
    setOpen(false);
    setReviewQuery('');
    setReviewOpen((current) => {
      const next = !current;
      if (next) logDataModels('app', 'governance_audience_selected_opened', { audience: label, count: selected.length });
      return next;
    });
  };

  return (
    <div ref={root} className="relative flex items-center gap-1">
      <div className={compact ? 'w-52' : 'min-w-0 flex-1'}>
        {hideLabel ? null : <label className={labelClass(isLight)}>{label}</label>}
        <button type="button" aria-label={hideLabel ? label : undefined} onClick={() => { setReviewOpen(false); setOpen((current) => !current); }} className={`${fieldClass(isLight)} flex items-center justify-between gap-2 text-left ${compact ? '!h-8 !rounded-none !py-0 !text-[13px]' : ''}`}>
          <span className={`truncate ${selected.length && !loading ? '' : isLight ? 'text-[#9AA1AB]' : 'text-slate-500'}`}>
            {buttonLabel}
          </span>
          <ChevronDown size={14} className={isLight ? 'text-[#6B7280]' : 'text-slate-400'} />
        </button>
        {open ? (
          <div className={`absolute z-30 mt-1 overflow-hidden rounded-none border shadow-md ${compact ? 'w-52' : 'w-full'} ${panel}`}>
            <div className="relative px-2 py-2">
              <Search size={13} className={`absolute left-4 top-1/2 -translate-y-1/2 ${muted}`} />
              <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Search ${label.toLowerCase()}`} className={`${fieldClass(isLight)} h-8 !rounded-none pl-8`} />
            </div>
            <div className="max-h-48 overflow-y-auto py-1">
              {loading ? (
                <p className={`px-3 py-2 text-[13px] ${muted}`}>Loading {label.toLowerCase()}…</p>
              ) : !visible.length ? (
                <p className={`px-3 py-2 text-[13px] ${muted}`}>{query.trim() ? 'No matches' : emptyText}</p>
              ) : visible.map((option) => {
                const checked = selectedIds.includes(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => onChange(checked ? selectedIds.filter((id) => id !== option.id) : [...selectedIds, option.id])}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] ${isLight ? 'hover:bg-[#F3F7FB]' : 'hover:bg-white/5'}`}
                  >
                    <span className={`inline-flex h-4 w-4 items-center justify-center rounded-none border ${checked ? 'border-[#1B6CA8] bg-[#1B6CA8] text-white' : isLight ? 'border-[#D8DCE2]' : 'border-white/20'}`}>
                      {checked ? <Check size={11} /> : null}
                    </span>
                    <span className="truncate">{option.label}</span>
                    {option.detail ? <span className={`ml-auto text-[11px] ${muted}`}>{option.detail}</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
      {selected.length ? (
        <div className="relative shrink-0">
          <button
            type="button"
            className={`${iconBtn(isLight)} ${compact ? '!h-8 !w-8 !rounded-none' : ''}`}
            aria-label={`View selected ${label.toLowerCase()}`}
            title={`View selected ${label.toLowerCase()}`}
            onClick={openReview}
          >
            <Users size={13} />
            <span className={`absolute -right-0.5 -top-0.5 min-w-[14px] rounded-none px-0.5 text-center text-[9px] leading-[14px] ${isLight ? 'bg-[#E7F2FB] text-[#1B6CA8]' : 'bg-white/15 text-slate-200'}`}>
              {selected.length}
            </span>
          </button>
          {reviewOpen ? (
            <div className={`absolute right-0 z-40 mt-1 w-56 overflow-hidden rounded-none border shadow-md ${panel}`}>
              <div className={`flex items-center justify-between px-2 py-1.5 text-[11px] ${isLight ? 'bg-[#F7F8FA] text-[#3C4048]' : 'bg-white/[0.04] text-slate-300'}`}>
                <span>{selected.length} selected</span>
              </div>
              <div className="relative px-2 py-1.5">
                <Search size={12} className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${muted}`} />
                <input
                  autoFocus
                  value={reviewQuery}
                  onChange={(event) => setReviewQuery(event.target.value)}
                  placeholder={`Filter ${label.toLowerCase()}`}
                  className={`${fieldClass(isLight)} h-7 !rounded-none !py-0 pl-7 text-[13px]`}
                />
              </div>
              <div className="max-h-44 overflow-y-auto py-1">
                {!reviewed.length ? (
                  <p className={`px-2.5 py-1.5 text-[12px] ${muted}`}>No matches</p>
                ) : reviewed.map((option) => (
                  <div key={option.id} className={`flex items-center gap-1.5 px-2.5 py-1 text-[13px] ${isLight ? 'hover:bg-[#F3F7FB]' : 'hover:bg-white/5'}`}>
                    <span className="min-w-0 flex-1 truncate">{option.label}</span>
                    {option.detail ? <span className={`text-[11px] ${muted}`}>{option.detail}</span> : null}
                    <button
                      type="button"
                      className={`${iconBtn(isLight)} !h-5 !w-5`}
                      aria-label={`Remove ${option.label}`}
                      onClick={() => onChange(selectedIds.filter((id) => id !== option.id))}
                    >
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function toggle(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
}

function DefinitionModeToggle({
  value,
  onChange,
  isLight,
  disabled,
}: {
  value: PolicyDefinitionMode;
  onChange: (mode: PolicyDefinitionMode) => void;
  isLight: boolean;
  disabled?: boolean;
}) {
  return (
    <div className={`flex rounded-lg border p-0.5 ${isLight ? 'border-[#E6E8EB]' : 'border-white/10'}`}>
      {([
        { id: 'simple', label: 'Classic' },
        { id: 'complex', label: 'Advanced' },
      ] as const).map((item) => (
        <button
          key={item.id}
          type="button"
          disabled={disabled}
          onClick={() => onChange(item.id)}
          className={`h-7 rounded-md px-2.5 text-[11px] ${
            value === item.id
              ? isLight ? 'bg-[#F3F4F6] font-medium text-[#111827]' : 'bg-white/10 font-medium text-white'
              : isLight ? 'text-[#6B7280]' : 'text-slate-400'
          }`}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

export function RowDefinitionCard({
  item,
  index,
  canRemove,
  onRemove,
  onChange,
  assets,
  isLight,
  readOnly,
  workbench,
}: {
  item: PolicyRule;
  index: number;
  canRemove: boolean;
  onRemove: () => void;
  onChange: (rule: PolicyRule) => void;
  assets: PolicyAsset[];
  isLight: boolean;
  readOnly?: boolean;
  workbench?: ExpressionWorkbenchProps;
}) {
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const mode = ruleDefinitionMode(item);
  const [open, setOpen] = useState(true);
  const [classicFallback] = useState(() => emptyGroup(item.id));
  const condition = item.condition ?? classicFallback;
  const rootGroup = condition.kind === 'group' ? condition : classicFallback;
  const setMode = (next: PolicyDefinitionMode) => {
    if (readOnly || next === mode) return;
    onChange({
      ...item,
      definitionMode: next,
      condition: item.condition ?? emptyGroup(item.id),
      expression: item.expression ?? '',
    });
    logDataModels('app', 'governance_rule_mode_changed', { ruleId: item.id, mode: next });
  };
  const setCondition = (next: typeof condition) => onChange({ ...item, condition: next });
  const workbenchProps = {
    variant: 'workbench' as const,
    policyType: 'ROW_LEVEL' as const,
    readOnly,
    testUserId: workbench?.testUserId,
    onTestUserId: workbench?.onTestUserId,
    onCopyNotice: workbench?.onCopyNotice,
    onOpenHelp: workbench?.onOpenHelp,
  };

  return (
    <article>
      <div className={`flex min-h-8 items-center gap-2 rounded-md px-2 py-1 ${isLight ? 'bg-[#F7F8FA]' : 'bg-white/[0.04]'}`}>
        <button
          type="button"
          aria-expanded={open}
          aria-label={open ? 'Collapse rule' : 'Expand rule'}
          className={iconBtn(isLight)}
          onClick={() => setOpen((current) => !current)}
        >
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>
        <p className={`min-w-0 flex-1 text-sm font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>
          Rule {index + 1}
          <span className={`ml-2 font-normal ${muted}`}>Applies to {subjectTypeLabel(item.subject)}</span>
        </p>
        {canRemove ? (
          <button type="button" disabled={readOnly} className={ghostBtn(isLight)} onClick={onRemove}>Remove</button>
        ) : null}
      </div>
      {open ? (
        <TreeChildren isLight={isLight}>
          <div className="flex min-h-8 flex-wrap items-center gap-2 py-0.5">
            <SubjectFields layout="row" subject={item.subject} onChange={(subject) => onChange({ ...item, subject })} isLight={isLight} assets={assets} />
          </div>
          <div>
            <div className="flex min-h-8 flex-wrap items-center gap-2 py-0.5">
              <span className={`${labelClass(isLight)} !mb-0 shrink-0`}>Rule conditions</span>
              <GroupActions
                group={rootGroup}
                onChange={setCondition}
                isLight={isLight}
                idPrefix={item.id}
                disabled={readOnly}
              />
              <DefinitionModeToggle value={mode} onChange={setMode} isLight={isLight} disabled={readOnly} />
            </div>
            {mode === 'simple' ? (
              <ConditionBuilder
                node={rootGroup}
                onChange={setCondition}
                assets={assets}
                isLight={isLight}
                idPrefix={item.id}
                hideRootToolbar
              />
            ) : (
              <TreeChildren isLight={isLight}>
                <ExpressionEditor
                  value={item.expression || ''}
                  onChange={(expression) => onChange({ ...item, expression })}
                  assets={assets}
                  isLight={isLight}
                  {...workbenchProps}
                />
              </TreeChildren>
            )}
          </div>
        </TreeChildren>
      ) : null}
    </article>
  );
}

export function ColumnDefinitionCard({
  item,
  index,
  canRemove,
  onRemove,
  onChange,
  assets,
  isLight,
  readOnly,
  workbench,
}: {
  item: PolicyRule;
  index: number;
  canRemove: boolean;
  onRemove: () => void;
  onChange: (rule: PolicyRule) => void;
  assets: PolicyAsset[];
  isLight: boolean;
  readOnly?: boolean;
  workbench?: ExpressionWorkbenchProps;
}) {
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const mode = ruleDefinitionMode(item);
  const [open, setOpen] = useState(true);
  const [classicFallback] = useState(() => emptyGroup(item.id));
  const condition = item.condition ?? classicFallback;
  const rootGroup = condition.kind === 'group' ? condition : classicFallback;
  const maskAction = item.action.type === 'COLUMN' || item.action.type === 'SENSITIVE' ? item.action : null;
  const setMode = (next: PolicyDefinitionMode) => {
    if (readOnly || next === mode) return;
    onChange({
      ...item,
      definitionMode: next,
      condition: item.condition ?? emptyGroup(item.id),
      expression: item.expression ?? '',
    });
    logDataModels('app', 'governance_rule_mode_changed', { ruleId: item.id, mode: next, type: 'COLUMN_LEVEL' });
  };
  const setCondition = (next: typeof condition) => onChange({ ...item, condition: next });
  const workbenchProps = {
    variant: 'workbench' as const,
    policyType: 'COLUMN_LEVEL' as const,
    readOnly,
    testUserId: workbench?.testUserId,
    onTestUserId: workbench?.onTestUserId,
    onCopyNotice: workbench?.onCopyNotice,
    onOpenHelp: workbench?.onOpenHelp,
  };

  return (
    <article>
      <div className={`flex min-h-8 items-center gap-2 rounded-md px-2 py-1 ${isLight ? 'bg-[#F7F8FA]' : 'bg-white/[0.04]'}`}>
        <button
          type="button"
          aria-expanded={open}
          aria-label={open ? 'Collapse rule' : 'Expand rule'}
          className={iconBtn(isLight)}
          onClick={() => setOpen((current) => !current)}
        >
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>
        <p className={`min-w-0 flex-1 text-sm font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>
          Rule {index + 1}
          <span className={`ml-2 font-normal ${muted}`}>Applies to {subjectTypeLabel(item.subject)}</span>
        </p>
        {canRemove ? (
          <button type="button" disabled={readOnly} className={ghostBtn(isLight)} onClick={onRemove}>Remove</button>
        ) : null}
      </div>
      {open ? (
        <TreeChildren isLight={isLight}>
          <div className="flex min-h-8 flex-wrap items-center gap-2 py-0.5">
            <SubjectFields layout="row" subject={item.subject} onChange={(subject) => onChange({ ...item, subject })} isLight={isLight} assets={assets} />
          </div>
          <div className="flex min-h-8 flex-col gap-1 py-0.5">
            <SubjectFields
              layout="row"
              label="Except"
              everyoneLabel="Nobody"
              subject={item.except ?? emptySubject()}
              onChange={(except) => onChange({ ...item, except })}
              isLight={isLight}
              assets={assets}
            />
            <p className={`text-[11px] ${muted}`}>These people are not masked, even when the condition matches.</p>
          </div>
          {maskAction ? (
            <MaskFields
              compact
              disabled={readOnly}
              mask={maskAction.mask}
              onChange={(mask) => onChange({ ...item, action: { ...maskAction, mask } })}
              isLight={isLight}
            />
          ) : null}
          <div>
            <div className="flex min-h-8 flex-wrap items-center gap-2 py-0.5">
              <span className={`${labelClass(isLight)} !mb-0 shrink-0`}>When this mask applies</span>
              <GroupActions
                group={rootGroup}
                onChange={setCondition}
                isLight={isLight}
                idPrefix={item.id}
                disabled={readOnly}
              />
              <DefinitionModeToggle value={mode} onChange={setMode} isLight={isLight} disabled={readOnly} />
            </div>
            <p className={`mb-1 text-[11px] ${muted}`}>Leave empty to always mask for this audience. Advanced accepts a when-condition or full mask SQL such as CASE WHEN employee_id = 'EMP0006' THEN value ELSE … END.</p>
            {mode === 'simple' ? (
              <ConditionBuilder
                node={rootGroup}
                onChange={setCondition}
                assets={assets}
                isLight={isLight}
                idPrefix={item.id}
                hideRootToolbar
              />
            ) : (
              <TreeChildren isLight={isLight}>
                <ExpressionEditor
                  value={item.expression || ''}
                  onChange={(expression) => onChange({ ...item, expression })}
                  assets={assets}
                  isLight={isLight}
                  {...workbenchProps}
                />
              </TreeChildren>
            )}
          </div>
        </TreeChildren>
      ) : null}
    </article>
  );
}

function updateRule(policy: GovernancePolicy, ruleId: string, patch: Partial<PolicyRule>): GovernancePolicy {
  return { ...policy, rules: policy.rules.map((rule) => (rule.id === ruleId ? { ...rule, ...patch } : rule)) };
}

function compactFieldClass(isLight: boolean): string {
  return `${fieldClass(isLight)} !h-8 !rounded-none !py-0 !text-[13px]`;
}

function restrictAudienceSubject(subject: SubjectSelector, policyType: PolicyType, complexity: 'simple' | 'complex'): SubjectSelector {
  if (policyType === 'ACCESS_CONTROL' && complexity === 'simple' && (subject.mode === 'attributes' || subject.mode === 'expression')) {
    return { ...subject, mode: 'roles' };
  }
  if (complexity === 'simple' && subject.mode === 'expression') {
    return { ...subject, mode: 'roles' };
  }
  return subject;
}

function MaskFields({
  mask,
  onChange,
  isLight,
  compact,
  disabled,
}: {
  mask: MaskAction;
  onChange: (mask: MaskAction) => void;
  isLight: boolean;
  compact?: boolean;
  disabled?: boolean;
}) {
  const field = compact ? compactFieldClass(isLight) : fieldClass(isLight);
  const label = compact ? `${labelClass(isLight)} !mb-0 shrink-0` : labelClass(isLight);
  return (
    <div className={compact ? 'ml-6 flex flex-wrap items-center gap-2' : 'grid gap-2 md:grid-cols-2'}>
      <div className={compact ? 'flex items-center gap-2' : undefined}>
        <label className={label}>Protection</label>
        <select disabled={disabled} className={compact ? `${field} w-52` : field} value={mask.kind} onChange={(event) => onChange({ ...mask, kind: event.target.value as MaskAction['kind'] })}>
          <option value="none">No extra mask</option>
          <option value="full_null">Full mask · NULL</option>
          <option value="full_fixed">Full mask · fixed value</option>
          <option value="full_string">Full string mask</option>
          <option value="partial_first">Partial · first N</option>
          <option value="partial_last">Partial · last N</option>
          <option value="partial_email">Partial · email</option>
          <option value="partial_phone">Partial · phone</option>
          <option value="partial_custom">Partial · custom pattern</option>
          <option value="deny_column">Deny column access</option>
          <option value="deny_asset">Deny asset access</option>
          <option value="custom_hash">Custom · hash</option>
          <option value="custom_expression">Custom · expression</option>
        </select>
      </div>
      {mask.kind === 'full_fixed' || mask.kind === 'partial_custom' || mask.kind === 'custom_expression' ? (
        <div className={compact ? 'flex items-center gap-2' : undefined}>
          <label className={label}>{mask.kind === 'custom_expression' ? 'Expression' : 'Value / pattern'}</label>
          <input disabled={disabled} className={compact ? `${field} w-40` : field} value={mask.value ?? mask.expression ?? ''} onChange={(event) => onChange({ ...mask, value: event.target.value, expression: event.target.value })} />
        </div>
      ) : null}
      {mask.kind === 'partial_first' || mask.kind === 'partial_last' ? (
        <div className={compact ? 'flex items-center gap-2' : undefined}>
          <label className={label}>Characters</label>
          <input disabled={disabled} type="number" min={1} className={compact ? `${field} w-20` : field} value={mask.n ?? 2} onChange={(event) => onChange({ ...mask, n: Number(event.target.value) })} />
        </div>
      ) : null}
    </div>
  );
}

function AudienceDefinitionCard({
  item,
  index,
  canRemove,
  onRemove,
  onChange,
  assets,
  isLight,
  readOnly,
  complexity,
  policyType,
  workbench,
}: {
  item: PolicyRule;
  index: number;
  canRemove: boolean;
  onRemove: () => void;
  onChange: (rule: PolicyRule) => void;
  assets: PolicyAsset[];
  isLight: boolean;
  readOnly?: boolean;
  complexity: 'simple' | 'complex';
  policyType: PolicyType;
  workbench?: ExpressionWorkbenchProps;
}) {
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const [open, setOpen] = useState(true);
  const compact = compactFieldClass(isLight);
  const subject = restrictAudienceSubject(item.subject, policyType, complexity);
  const title = policyType === 'ACCESS_CONTROL' ? `Access ${index + 1}` : `Rule ${index + 1}`;
  const accessAction = item.action.type === 'ACCESS' ? item.action : null;
  const maskAction = item.action.type === 'COLUMN' || item.action.type === 'SENSITIVE' ? item.action : null;

  return (
    <article>
      <div className={`flex min-h-8 items-center gap-2 rounded-md px-2 py-1 ${isLight ? 'bg-[#F7F8FA]' : 'bg-white/[0.04]'}`}>
        <button
          type="button"
          aria-expanded={open}
          aria-label={open ? 'Collapse rule' : 'Expand rule'}
          className={iconBtn(isLight)}
          onClick={() => setOpen((current) => !current)}
        >
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>
        <p className={`min-w-0 flex-1 text-sm font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>
          {title}
          <span className={`ml-2 font-normal ${muted}`}>Applies to {subjectTypeLabel(subject)}</span>
        </p>
        {canRemove ? (
          <button type="button" disabled={readOnly} className={ghostBtn(isLight)} onClick={onRemove}>Remove</button>
        ) : null}
      </div>
      {open ? (
        <TreeChildren isLight={isLight}>
          <div className="flex min-h-8 flex-wrap items-center gap-2 py-0.5">
            <SubjectFields layout="row" subject={subject} onChange={(next) => onChange({ ...item, subject: next })} isLight={isLight} assets={assets} />
            {maskAction ? (
              <MaskFields
                compact
                disabled={readOnly}
                mask={maskAction.mask}
                onChange={(mask) => onChange({ ...item, action: { ...maskAction, mask } })}
                isLight={isLight}
              />
            ) : null}
          </div>
          {accessAction ? (
            <div className="flex min-h-8 flex-wrap items-center gap-2 py-0.5">
              <span className={`${labelClass(isLight)} !mb-0 shrink-0`}>They</span>
              <select
                disabled={readOnly}
                aria-label="They"
                className={`${compact} w-28`}
                value={accessAction.effect}
                onChange={(event) => onChange({ ...item, action: { type: 'ACCESS', effect: event.target.value as 'allow' | 'deny', operations: accessAction.operations } })}
              >
                <option value="allow">can</option>
                <option value="deny">cannot</option>
              </select>
              {complexity === 'simple' ? (
                <>
                  <span className={`${labelClass(isLight)} !mb-0 shrink-0`}>Actions</span>
                  <select
                    disabled={readOnly}
                    aria-label="Actions"
                    className={`${compact} w-52`}
                    value={accessAction.operations[0] ?? 'SELECT'}
                    onChange={(event) => onChange({ ...item, action: { type: 'ACCESS', effect: accessAction.effect, operations: [event.target.value as AccessOperation] } })}
                  >
                    {OPERATIONS.map((operation) => <option key={operation} value={operation}>{OPERATION_LABEL[operation]} ({operation})</option>)}
                  </select>
                </>
              ) : (
                <div className={`flex min-w-[220px] flex-1 flex-wrap gap-x-3 gap-y-1 ${isLight ? 'text-[#1C1C1C]' : 'text-slate-100'}`}>
                  {OPERATIONS.map((operation) => {
                    const checked = accessAction.operations.includes(operation);
                    return (
                      <label key={operation} className="inline-flex items-center gap-1.5 text-[13px]">
                        <input
                          type="checkbox"
                          disabled={readOnly}
                          checked={checked}
                          onChange={() => onChange({ ...item, action: { type: 'ACCESS', effect: accessAction.effect, operations: toggle(accessAction.operations, operation) as AccessOperation[] } })}
                        />
                        <span>{OPERATION_LABEL[operation]}</span>
                        <span className={`font-mono text-[11px] ${muted}`}>{operation}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}
          {complexity === 'complex' ? (
            <div className="space-y-1 py-0.5">
              <span className={`${labelClass(isLight)} !mb-0`}>Condition (optional)</span>
              <ExpressionEditor
                value={item.expression ?? ''}
                onChange={(expression) => onChange({ ...item, expression })}
                assets={assets}
                isLight={isLight}
                policyType={policyType}
                readOnly={readOnly}
              />
            </div>
          ) : null}
        </TreeChildren>
      ) : null}
    </article>
  );
}

export function TypeSpecificFields({
  policy,
  onChange,
  assets,
  isLight,
}: {
  policy: GovernancePolicy;
  onChange: (policy: GovernancePolicy) => void;
  assets: PolicyAsset[];
  isLight: boolean;
}) {
  const selected = assets.filter((asset) => policy.target.mode === 'all' || policy.target.assetIds.includes(asset.id) || policy.target.mode === 'dynamic' || policy.target.mode === 'current');
  const rowAssets = assetsForRowFilter(policy, assets);
  const { users: directoryUsers } = usePolicyDirectory();
  const rule = policy.rules[0];
  return (
    <div className="space-y-3">
      <Section title="Targets">
        <TargetPicker policy={policy} onChange={onChange} assets={assets} isLight={isLight} lockCurrent={policy.scope.level === 'ASSET'} />
        {policy.type === 'COLUMN_LEVEL' ? (
          <ColumnPicker policy={policy} onChange={onChange} assets={selected.length ? selected : assets} isLight={isLight} />
        ) : null}
      </Section>

      {policy.type === 'ROW_LEVEL' ? (
        <Section title="Who this applies to">
          <p className={`text-xs ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>Each card is one rule. Classic uses the condition builder. Advanced uses an expression and query builder for that audience only.</p>
          {(policy.rules.length ? policy.rules : [rule]).map((item, index) => (
            <RowDefinitionCard
              key={item.id}
              item={item}
              index={index}
              canRemove={policy.rules.length > 1}
              onRemove={() => onChange({ ...policy, rules: policy.rules.filter((entry) => entry.id !== item.id) })}
              onChange={(next) => onChange(updateRule(policy, item.id, next))}
              assets={rowAssets}
              isLight={isLight}
            />
          ))}
          <button
            type="button"
            className={ghostBtn(isLight)}
            onClick={() => {
              const next = defaultRule('ROW_LEVEL');
              onChange({ ...policy, rules: [...policy.rules, next] });
              logDataModels('app', 'governance_rule_added', { type: 'ROW_LEVEL', ruleId: next.id });
            }}
          >
            Add rule
          </button>
        </Section>
      ) : null}

      {policy.type === 'COLUMN_LEVEL' ? (
        <Section title="Who this applies to">
          <p className={`text-xs ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>Each card is one rule with its own audience and mask. Classic / Advanced control when that mask applies. Leave when empty to always mask.</p>
          {(policy.rules.length ? policy.rules : [rule]).map((item, index) => (
            <ColumnDefinitionCard
              key={item.id}
              item={item}
              index={index}
              canRemove={policy.rules.length > 1}
              onRemove={() => onChange({ ...policy, rules: policy.rules.filter((entry) => entry.id !== item.id) })}
              onChange={(next) => onChange(updateRule(policy, item.id, next))}
              assets={selected.length ? selected : assets}
              isLight={isLight}
            />
          ))}
          <button
            type="button"
            className={ghostBtn(isLight)}
            onClick={() => {
              const next = defaultRule('COLUMN_LEVEL');
              onChange({ ...policy, rules: [...policy.rules, next] });
              logDataModels('app', 'governance_rule_added', { type: 'COLUMN_LEVEL', ruleId: next.id });
            }}
          >
            Add rule
          </button>
        </Section>
      ) : null}

      {policy.type !== 'ROW_LEVEL' && policy.type !== 'COLUMN_LEVEL' ? (
        <Section title={policy.type === 'ACCESS_CONTROL' ? 'Who can do what' : policy.type === 'TIME_BASED' ? 'When access applies' : 'Who this applies to'}>
          <p className={`text-xs ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>
            {policy.type === 'ACCESS_CONTROL'
              ? 'Each card is one audience. Deny always wins. If no Allow matches, access is denied.'
              : policy.type === 'TIME_BASED'
                ? 'A time-based Allow cannot override a stronger Deny from another policy.'
                : 'Each card is one audience. Blocking a column or asset wins over any mask. No extra mask lets that audience see the clear value.'}
          </p>
          <div className="space-y-3">
            {policy.rules.map((item, index) => (
              <article key={item.id} className={`rounded-md border p-3 ${isLight ? 'border-[#E6E8EB] bg-[#F7F8FA]' : 'border-white/10 bg-white/[0.03]'}`}>
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <p className={`text-[11px] font-medium uppercase tracking-wide ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>
                      {policy.type === 'ACCESS_CONTROL' ? `Access ${index + 1}` : `Audience ${index + 1}`}
                    </p>
                    <p className={`mt-0.5 text-sm font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{summarizeRule(item, directoryUsers)}</p>
                  </div>
                  {policy.rules.length > 1 ? (
                    <button type="button" className={ghostBtn(isLight)} onClick={() => onChange({ ...policy, rules: policy.rules.filter((entry) => entry.id !== item.id) })}>Remove</button>
                  ) : null}
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <SubjectFields subject={item.subject} onChange={(subject) => onChange(updateRule(policy, item.id, { subject }))} isLight={isLight} assets={selected} />
                  <div className="space-y-3">
                    {item.action.type === 'ACCESS' ? (
                      <>
                        <div>
                          <label className={labelClass(isLight)}>They</label>
                          <select className={fieldClass(isLight)} value={item.action.effect} onChange={(event) => onChange(updateRule(policy, item.id, { action: { type: 'ACCESS', effect: event.target.value as 'allow' | 'deny', operations: item.action.type === 'ACCESS' ? item.action.operations : ['SELECT'] } }))}>
                            <option value="allow">can</option>
                            <option value="deny">cannot</option>
                          </select>
                        </div>
                        <div>
                          <label className={labelClass(isLight)}>These actions</label>
                          <div className={`divide-y rounded-md border ${isLight ? 'divide-[#E6E8EB] border-[#E6E8EB] bg-white' : 'divide-white/10 border-white/10 bg-black'}`}>
                            {OPERATIONS.map((operation) => {
                              const checked = item.action.type === 'ACCESS' && item.action.operations.includes(operation);
                              return (
                                <label key={operation} className="flex items-center gap-2 px-3 py-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => {
                                      const current = item.action.type === 'ACCESS' ? item.action.operations : [];
                                      onChange(updateRule(policy, item.id, { action: { type: 'ACCESS', effect: item.action.type === 'ACCESS' ? item.action.effect : 'allow', operations: toggle(current, operation) as AccessOperation[] } }));
                                    }}
                                  />
                                  <span>{OPERATION_LABEL[operation]}</span>
                                  <span className={`ml-auto font-mono text-[11px] ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>{operation}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    ) : null}
                    {item.action.type === 'COLUMN' || item.action.type === 'SENSITIVE' ? (
                      <MaskFields mask={item.action.mask} onChange={(mask) => onChange(updateRule(policy, item.id, { action: { ...item.action, mask } as PolicyRule['action'] }))} isLight={isLight} />
                    ) : null}
                    {item.action.type === 'TIME' ? <TimeFields policy={policy} rule={item} onChange={onChange} isLight={isLight} /> : null}
                  </div>
                </div>
              </article>
            ))}
          </div>
          {policy.type === 'ACCESS_CONTROL' || policy.type === 'SENSITIVE_DATA' ? (
            <button type="button" className={ghostBtn(isLight)} onClick={() => onChange({ ...policy, rules: [...policy.rules, defaultRule(policy.type)] })}>
              Add another audience
            </button>
          ) : null}
        </Section>
      ) : null}

      {policy.type === 'SENSITIVE_DATA' ? (
        <div className="flex flex-wrap gap-2">
          <PresetButton isLight={isLight} label="Protect all PII" onClick={() => applyPreset(policy, onChange, 'SENSITIVE_DATA', ['PII'], 'full_string', { mode: 'roles', roles: ['analyst'] })} />
          <PresetButton isLight={isLight} label="Restrict payroll" onClick={() => applyPreset(policy, onChange, 'SENSITIVE_DATA', ['Restricted'], 'deny_asset', { mode: 'attributes', attribute: { key: 'department', operator: 'neq', value: 'HR' } })} />
          <PresetButton isLight={isLight} label="Protect payment PCI" onClick={() => applyPreset(policy, onChange, 'SENSITIVE_DATA', ['PCI'], 'full_string', { mode: 'groups', groups: ['External'] })} />
        </div>
      ) : null}

      {policy.type === 'TIME_BASED' ? (
        <div className="flex flex-wrap gap-2">
          <button type="button" className={ghostBtn(isLight)} onClick={() => onChange(contractorPreset(policy))}>Contractor expiration</button>
          <button type="button" className={ghostBtn(isLight)} onClick={() => onChange(businessHoursPreset(policy))}>Business hours</button>
        </div>
      ) : null}

      <Section title="Advanced expression">
        <p className={`text-xs ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>WorkSpace DSL with IntelliSense. Type ${'{'} for variables. This is a frontend simulation, not query-engine enforcement.</p>
        <ExpressionEditor
          value={policy.advancedExpression}
          onChange={(advancedExpression) => onChange({ ...policy, advancedExpression })}
          assets={selected.length ? selected : assets}
          isLight={isLight}
          policyType={policy.type}
          variant="workbench"
        />
      </Section>
    </div>
  );
}

function columnLabel(asset: Pick<PolicyAsset, 'name' | 'path'> | undefined, columnName: string): string {
  return `${assetFieldLabel(asset ?? { name: 'asset', path: '' })}.${columnName}`;
}

export function ColumnPicker({
  policy,
  onChange,
  assets,
  isLight,
  disabled,
}: {
  policy: GovernancePolicy;
  onChange: (policy: GovernancePolicy) => void;
  assets: PolicyAsset[];
  isLight: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [coords, setCoords] = useState({ top: 0, bottom: 0, left: 0, width: 320, maxHeight: 320, openUp: false });
  const root = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { assets: withColumns, loading, error: loadError } = usePolicyAssetColumns(assets, { enabled: open && !disabled });
  const options = useMemo(() => withColumns.flatMap((asset) => {
    const group = assetFieldLabel(asset);
    return asset.columns.map((column) => ({
      id: `${asset.id}:${column.name}`,
      assetId: asset.id,
      column: column.name,
      group,
      label: columnLabel(asset, column.name),
      name: column.name,
      detail: `${column.type} · ${column.classification ?? 'Unclassified'}`,
    }));
  }), [withColumns]);
  const selectedIds = policy.target.columnRefs.map((item) => `${item.assetId}:${item.column}`);
  const selected = policy.target.columnRefs.map((ref) => {
    const option = options.find((item) => item.assetId === ref.assetId && item.column === ref.column);
    if (option) return option;
    const asset = assets.find((item) => item.id === ref.assetId);
    return {
      id: `${ref.assetId}:${ref.column}`,
      assetId: ref.assetId,
      column: ref.column,
      group: assetFieldLabel(asset ?? { name: 'asset', path: '' }),
      label: columnLabel(asset, ref.column),
      name: ref.column,
      detail: '',
    };
  });
  const visible = options.filter((option) => `${option.label} ${option.group} ${option.name} ${option.detail}`.toLowerCase().includes(query.trim().toLowerCase()));
  const visibleGroups = useMemo(() => {
    const map = new Map<string, { id: string; label: string; options: typeof visible }>();
    visible.forEach((option) => {
      const current = map.get(option.assetId) ?? { id: option.assetId, label: option.group, options: [] };
      current.options.push(option);
      map.set(option.assetId, current);
    });
    return Array.from(map.values());
  }, [visible]);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const place = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const margin = 8;
      const width = Math.min(Math.max(rect.width, 280), window.innerWidth - margin * 2);
      const left = Math.min(Math.max(margin, rect.left), window.innerWidth - width - margin);
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      const openUp = spaceBelow < 240 && spaceAbove > spaceBelow;
      const maxHeight = Math.min(420, Math.max(0, openUp ? spaceAbove : spaceBelow));
      setCoords({
        top: rect.bottom + 4,
        bottom: window.innerHeight - rect.top + 4,
        left,
        width,
        maxHeight,
        openUp,
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (root.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (id: string) => {
    const option = options.find((item) => item.id === id) || selected.find((item) => item.id === id);
    if (!option) return;
    const next = selectedIds.includes(id)
      ? policy.target.columnRefs.filter((item) => `${item.assetId}:${item.column}` !== id)
      : [...policy.target.columnRefs, { assetId: option.assetId, column: option.column }];
    onChange({ ...policy, target: { ...policy.target, columnRefs: next } });
  };

  const summary = selected.length
    ? selected.length === 1
      ? selected[0].label
      : `${selected.length} columns selected`
    : 'Select columns';
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';

  return (
    <div ref={root} className="relative">
      <label className={labelClass(isLight)}>Columns</label>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={`${fieldClass(isLight)} flex items-center justify-between gap-2 text-left ${disabled ? 'opacity-70' : ''}`}
      >
        <span className={selected.length ? '' : isLight ? 'text-[#9AA1AB]' : 'text-slate-500'}>{summary}</span>
        <ChevronDown size={14} className={isLight ? 'text-[#6B7280]' : 'text-slate-400'} />
      </button>
      {selected.length ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selected.map((option) => (
            <span key={option.id} className={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-mono text-[11px] ${isLight ? 'bg-[#F2F4F7] text-[#3C4048]' : 'bg-white/10 text-slate-200'}`}>
              {option.label}
              {!disabled ? (
                <button type="button" className={`${iconBtn(isLight)} !h-5 !w-5`} aria-label={`Remove ${option.label}`} onClick={() => toggle(option.id)}>
                  <X size={11} />
                </button>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}
      {open && !disabled
        ? createPortal(
          <div
            ref={menuRef}
            role="listbox"
            style={{
              left: coords.left,
              width: coords.width,
              maxHeight: coords.maxHeight,
              ...(coords.openUp ? { bottom: coords.bottom } : { top: coords.top }),
            }}
            className={`fixed z-[240] flex flex-col overflow-hidden rounded-md border shadow-md ${isLight ? 'border-[#E6E8EB] bg-white shadow-slate-200/80' : 'border-white/10 bg-black'}`}
          >
            <div className="relative shrink-0 border-b px-2 py-2">
              <Search size={13} className={`absolute left-4 top-1/2 -translate-y-1/2 ${muted}`} />
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search table or column"
                className={isLight
                  ? 'h-8 w-full rounded-md border border-[#D8DCE2] bg-white py-0 pl-8 pr-3 text-sm text-[#1C1C1C] placeholder:text-[#9AA1AB] focus:border-[#1B6CA8] focus:outline-none'
                  : 'h-8 w-full rounded-md border border-white/15 bg-black py-0 pl-8 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-white/30 focus:outline-none'}
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1">
              {visibleGroups.map((group) => (
                <div key={group.id}>
                  <p className={`sticky top-0 px-3 py-1.5 text-[11px] font-medium ${isLight ? 'bg-[#F7F8FA] text-[#6B7280]' : 'bg-black text-slate-400'}`}>
                    {group.label}
                  </p>
                  {group.options.map((option) => {
                    const checked = selectedIds.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => toggle(option.id)}
                        className={`flex w-full items-center gap-2 px-3 py-1.5 pl-5 text-left ${isLight ? 'hover:bg-[#F3F7FB]' : 'hover:bg-white/5'}`}
                      >
                        <span className={`inline-flex h-4 w-4 items-center justify-center rounded border ${checked ? 'border-[#1B6CA8] bg-[#1B6CA8] text-white' : isLight ? 'border-[#D8DCE2]' : 'border-white/20'}`}>
                          {checked ? <Check size={11} /> : null}
                        </span>
                        <span className={`font-mono text-xs ${isLight ? 'text-[#1C1C1C]' : 'text-slate-100'}`}>{option.name}</span>
                        <span className={`ml-auto text-[11px] ${muted}`}>{option.detail}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
              {loading ? <p className={`px-3 py-3 text-xs ${muted}`}>Loading columns…</p> : null}
              {loadError ? <p className={`px-3 py-3 text-xs ${isLight ? 'text-rose-600' : 'text-rose-300'}`}>{loadError}</p> : null}
              {!loading && !loadError && !visible.length ? <p className={`px-3 py-3 text-xs ${muted}`}>No columns match this search.</p> : null}
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}

function TimeFields({ policy, rule, onChange, isLight }: { policy: GovernancePolicy; rule: PolicyRule; onChange: (policy: GovernancePolicy) => void; isLight: boolean }) {
  if (rule.action.type !== 'TIME') return null;
  const action = rule.action;
  const window = action.window;
  const setWindow = (patch: Partial<typeof window>) => onChange(updateRule(policy, rule.id, { action: { type: 'TIME', effect: action.effect, window: { ...window, ...patch } } }));
  return (
    <div className="grid gap-2 md:grid-cols-2">
      <div>
        <label className={labelClass(isLight)}>Effect</label>
        <select className={fieldClass(isLight)} value={action.effect} onChange={(event) => onChange(updateRule(policy, rule.id, { action: { type: 'TIME', effect: event.target.value as 'allow_window' | 'deny_window' | 'expire', window } }))}>
          <option value="allow_window">Allow during window</option>
          <option value="deny_window">Deny during window</option>
          <option value="expire">Expire after date</option>
        </select>
      </div>
      <div>
        <label className={labelClass(isLight)}>Time zone</label>
        <select className={fieldClass(isLight)} value={window.timeZone} onChange={(event) => setWindow({ timeZone: event.target.value })}>
          <option value="America/New_York">America/New_York</option>
          <option value="UTC">UTC</option>
          <option value="Europe/Berlin">Europe/Berlin</option>
          <option value="Asia/Tokyo">Asia/Tokyo</option>
        </select>
      </div>
      <div>
        <label className={labelClass(isLight)}>Start</label>
        <input type="datetime-local" className={fieldClass(isLight)} value={toLocal(window.start)} onChange={(event) => setWindow({ start: fromLocal(event.target.value) })} />
      </div>
      <div>
        <label className={labelClass(isLight)}>End / expiration</label>
        <input type="datetime-local" className={fieldClass(isLight)} value={toLocal(window.end)} onChange={(event) => setWindow({ end: fromLocal(event.target.value) })} />
      </div>
      <div>
        <label className={labelClass(isLight)}>Daily start</label>
        <input type="time" className={fieldClass(isLight)} value={window.dailyStart} onChange={(event) => setWindow({ dailyStart: event.target.value })} />
      </div>
      <div>
        <label className={labelClass(isLight)}>Daily end</label>
        <input type="time" className={fieldClass(isLight)} value={window.dailyEnd} onChange={(event) => setWindow({ dailyEnd: event.target.value })} />
      </div>
      <div className="md:col-span-2">
        <label className={labelClass(isLight)}>Days</label>
        <div className="flex flex-wrap gap-1.5">
          {WEEKDAYS.map((day) => (
            <button key={day.id} type="button" className={ghostBtn(isLight)} onClick={() => setWindow({ daysOfWeek: toggle(window.daysOfWeek.map(String), String(day.id)).map(Number) })}>
              {window.daysOfWeek.includes(day.id) ? `✓ ${day.label}` : day.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function toLocal(value: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function fromLocal(value: string): string {
  return value ? new Date(value).toISOString() : '';
}

function PresetButton({ label, onClick, isLight }: { label: string; onClick: () => void; isLight: boolean }) {
  return <button type="button" className={ghostBtn(isLight)} onClick={onClick}>{label}</button>;
}

function applyPreset(
  policy: GovernancePolicy,
  onChange: (policy: GovernancePolicy) => void,
  type: PolicyType,
  classifications: Classification[],
  kind: MaskAction['kind'],
  subject: Partial<SubjectSelector>,
) {
  const rule = defaultRule(type);
  onChange({
    ...policy,
    type,
    target: { ...policy.target, mode: 'dynamic', match: { ...policy.target.match, classifications } },
    rules: [{
      ...rule,
      subject: { ...rule.subject, ...subject, userIds: subject.userIds ?? [], roles: subject.roles ?? [], groups: subject.groups ?? [] },
      action: { type: 'SENSITIVE', mask: { kind } },
    }],
    advancedExpression: EXAMPLE_EXPRESSIONS.SENSITIVE_DATA,
  });
}

function contractorPreset(policy: GovernancePolicy): GovernancePolicy {
  const rule = defaultRule('TIME_BASED');
  return {
    ...policy,
    name: policy.name || 'Contractor Access Expiration',
    rules: [{
      ...rule,
      subject: { ...rule.subject, mode: 'groups', groups: ['Contractors'] },
      action: { type: 'TIME', effect: 'expire', window: { start: '', end: '2026-11-30T23:59:59Z', daysOfWeek: [], dailyStart: '', dailyEnd: '', timeZone: 'America/New_York' } },
    }],
  };
}

function businessHoursPreset(policy: GovernancePolicy): GovernancePolicy {
  const rule = defaultRule('TIME_BASED');
  return {
    ...policy,
    name: policy.name || 'Business Hours Access',
    rules: [{
      ...rule,
      subject: { ...rule.subject, mode: 'groups', groups: ['External'] },
      action: { type: 'TIME', effect: 'allow_window', window: { start: '', end: '', daysOfWeek: [1, 2, 3, 4, 5], dailyStart: '09:00', dailyEnd: '18:00', timeZone: 'America/New_York' } },
    }],
  };
}

export function AppliesToSection({
  policy,
  onChange,
  assets,
  isLight,
  complexity,
  readOnly,
}: {
  policy: GovernancePolicy;
  onChange: (policy: GovernancePolicy) => void;
  assets: PolicyAsset[];
  isLight: boolean;
  complexity: 'simple' | 'complex';
  readOnly?: boolean;
}) {
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const ownerLabel = policy.scope.level === 'VAULT' ? 'this WorkSpace' : policy.scope.level === 'MODEL' ? 'this data model' : 'this table';
  const setMode = (mode: GovernancePolicy['target']['mode']) => {
    if (readOnly) return;
    onChange({ ...policy, target: { ...policy.target, mode } });
  };
  const columnAssets = assets.filter((asset) => policy.target.mode === 'all' || policy.target.mode === 'dynamic' || policy.target.assetIds.includes(asset.id) || !policy.target.assetIds.length);

  if (policy.scope.level === 'ASSET') {
    return (
      <Section title="Applies to">
        <p className={`text-xs ${muted}`}>This policy is owned by {assets[0]?.name ?? 'the current table'}. Targeting stays on that table.</p>
        {policy.type === 'COLUMN_LEVEL' || policy.type === 'SENSITIVE_DATA' ? (
          <ColumnPicker policy={policy} onChange={readOnly ? () => undefined : onChange} assets={assets} isLight={isLight} disabled={readOnly} />
        ) : null}
        {policy.type === 'SENSITIVE_DATA' ? (
          <div className="mt-3">
            <label className={labelClass(isLight)}>Classifications</label>
            <div className="flex flex-wrap gap-1.5">
              {CLASSIFICATIONS.map((item) => (
                <button
                  key={item}
                  type="button"
                  disabled={readOnly}
                  className={ghostBtn(isLight)}
                  onClick={() => {
                    const current = policy.target.match.classifications;
                    const classifications = current.includes(item) ? current.filter((entry) => entry !== item) : [...current, item];
                    onChange({ ...policy, target: { ...policy.target, match: { ...policy.target.match, classifications } } });
                  }}
                >
                  {policy.target.match.classifications.includes(item) ? `✓ ${item}` : item}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </Section>
    );
  }

  return (
    <Section title="Applies to">
      <p className={`text-xs ${muted}`}>
        {policy.type === 'ROW_LEVEL'
          ? `Choose which tables in ${ownerLabel} receive this row filter.`
          : policy.type === 'COLUMN_LEVEL'
            ? `Choose columns in ${ownerLabel} to protect.`
            : policy.type === 'ACCESS_CONTROL'
              ? `Choose which assets in ${ownerLabel} this access rule covers.`
              : `Choose classified fields in ${ownerLabel} to protect.`}
      </p>
      {policy.type === 'SENSITIVE_DATA' && complexity === 'simple' ? (
        <div className="flex flex-wrap gap-1.5">
          {CLASSIFICATIONS.map((item) => (
            <button
              key={item}
              type="button"
              disabled={readOnly}
              className={ghostBtn(isLight)}
              onClick={() => {
                const current = policy.target.match.classifications;
                const classifications = current.includes(item) ? current.filter((entry) => entry !== item) : [...current, item];
                onChange({ ...policy, target: { ...policy.target, mode: 'dynamic', match: { ...policy.target.match, classifications } } });
              }}
            >
              {policy.target.match.classifications.includes(item) ? `✓ ${item}` : item}
            </button>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {(['all', 'selected', ...(complexity === 'complex' || policy.type === 'SENSITIVE_DATA' || policy.type === 'COLUMN_LEVEL' ? ['dynamic'] as const : [])] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              disabled={readOnly}
              className={policy.target.mode === mode ? `${ghostBtn(isLight)} ring-1 ring-[#1B6CA8]` : ghostBtn(isLight)}
              onClick={() => setMode(mode)}
            >
              {mode === 'all' ? `All assets in ${ownerLabel}` : mode === 'selected' ? 'Selected assets' : 'Match conditions'}
            </button>
          ))}
        </div>
      )}
      {policy.target.mode === 'selected' ? (
        <AssetMultiSelect assets={assets} selectedIds={policy.target.assetIds} onChange={(assetIds) => onChange({ ...policy, target: { ...policy.target, assetIds, mode: 'selected' } })} isLight={isLight} disabled={readOnly} />
      ) : null}
      {policy.type === 'COLUMN_LEVEL' ? (
        <ColumnPicker policy={policy} onChange={readOnly ? () => undefined : onChange} assets={columnAssets} isLight={isLight} disabled={readOnly} />
      ) : null}
      {policy.target.mode === 'dynamic' && !readOnly ? (
        <DynamicMatchFields policy={policy} onChange={onChange} isLight={isLight} />
      ) : null}
    </Section>
  );
}

export function PolicyDefinitionFields({
  policy,
  onChange,
  assets,
  isLight,
  complexity,
  readOnly,
  workbench,
}: {
  policy: GovernancePolicy;
  onChange: (policy: GovernancePolicy) => void;
  assets: PolicyAsset[];
  isLight: boolean;
  complexity: 'simple' | 'complex';
  readOnly?: boolean;
  workbench?: ExpressionWorkbenchProps;
}) {
  const selected = assets.filter((asset) => policy.target.mode === 'all' || policy.target.mode === 'dynamic' || policy.target.mode === 'current' || policy.target.assetIds.includes(asset.id));
  const scoped = selected.length ? selected : assets;
  const rowAssets = assetsForRowFilter(policy, assets);
  const audienceTypes = policy.type === 'ACCESS_CONTROL' || policy.type === 'SENSITIVE_DATA';
  const columnSource = policy.type === 'ROW_LEVEL' || policy.type === 'COLUMN_LEVEL' ? rowAssets : scoped;
  const { assets: assetsWithColumns } = usePolicyAssetColumns(columnSource, { enabled: policy.type === 'ROW_LEVEL' || policy.type === 'COLUMN_LEVEL' || audienceTypes });
  const { users: directoryUsers } = usePolicyDirectory();
  const rule = policy.rules[0] ?? defaultRule(policy.type);
  const change = readOnly ? () => undefined : onChange;
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const workbenchProps = workbench
    ? {
        variant: 'workbench' as const,
        policyType: policy.type,
        readOnly,
        testUserId: workbench.testUserId,
        onTestUserId: workbench.onTestUserId,
        testResult: workbench.testResult,
        onRunTest: workbench.onRunTest,
        onCopyNotice: workbench.onCopyNotice,
        onOpenHelp: workbench.onOpenHelp,
      }
    : { variant: 'workbench' as const, policyType: policy.type, readOnly };

  if (policy.type === 'ROW_LEVEL') {
    const rowRules = policy.rules.length ? policy.rules : [rule];
    return (
      <div className="space-y-3">
        <p className={`text-xs ${muted}`}>Each card is one rule with its own audience. Classic uses the condition builder. Advanced uses an expression and query builder. Matching rules combine with AND.</p>
        {rowRules.map((item, index) => (
          <RowDefinitionCard
            key={item.id}
            item={item}
            index={index}
            canRemove={rowRules.length > 1}
            onRemove={() => change({ ...policy, rules: policy.rules.filter((entry) => entry.id !== item.id) })}
            onChange={(next) => change(updateRule(policy, item.id, next))}
            assets={assetsWithColumns}
            isLight={isLight}
            readOnly={readOnly}
            workbench={workbench}
          />
        ))}
        <button
          type="button"
          disabled={readOnly}
          className={ghostBtn(isLight)}
          onClick={() => {
            const next = defaultRule('ROW_LEVEL');
            change({ ...policy, rules: [...policy.rules, next] });
            logDataModels('app', 'governance_rule_added', { type: 'ROW_LEVEL', ruleId: next.id });
          }}
        >
          Add rule
        </button>
      </div>
    );
  }

  if (policy.type === 'COLUMN_LEVEL') {
    const columnRules = policy.rules.length ? policy.rules : [rule];
    return (
      <div className="space-y-3">
        <p className={`text-xs ${muted}`}>Each card is one rule with its own audience and mask. Classic builds when the mask applies. Advanced uses an expression. Leave when empty to always mask.</p>
        {columnRules.map((item, index) => (
          <ColumnDefinitionCard
            key={item.id}
            item={item}
            index={index}
            canRemove={columnRules.length > 1}
            onRemove={() => change({ ...policy, rules: policy.rules.filter((entry) => entry.id !== item.id) })}
            onChange={(next) => change(updateRule(policy, item.id, next))}
            assets={assetsWithColumns}
            isLight={isLight}
            readOnly={readOnly}
            workbench={workbench}
          />
        ))}
        <button
          type="button"
          disabled={readOnly}
          className={ghostBtn(isLight)}
          onClick={() => {
            const next = defaultRule('COLUMN_LEVEL');
            change({ ...policy, rules: [...policy.rules, next] });
            logDataModels('app', 'governance_rule_added', { type: 'COLUMN_LEVEL', ruleId: next.id });
          }}
        >
          <Plus size={14} />
          Add rule
        </button>
      </div>
    );
  }

  if (audienceTypes) {
    return (
      <div className="space-y-3">
        <p className={`text-xs ${muted}`}>
          {policy.type === 'ACCESS_CONTROL'
            ? complexity === 'simple'
              ? 'Each rule is one audience. Deny always wins.'
              : 'Use attributes or expressions for more precise audiences. Deny always wins.'
            : complexity === 'simple' ? 'Mask classified fields for everyone except a chosen audience.' : 'Add exceptions, patterns, and custom expressions.'}
        </p>
        {policy.rules.map((item, index) => (
          <AudienceDefinitionCard
            key={item.id}
            item={item}
            index={index}
            canRemove={policy.rules.length > 1}
            onRemove={() => change({ ...policy, rules: policy.rules.filter((entry) => entry.id !== item.id) })}
            onChange={(next) => change(updateRule(policy, item.id, next))}
            assets={assetsWithColumns}
            isLight={isLight}
            readOnly={readOnly}
            complexity={complexity}
            policyType={policy.type}
            workbench={workbench}
          />
        ))}
        <button
          type="button"
          disabled={readOnly}
          className={ghostBtn(isLight)}
          onClick={() => {
            const next = defaultRule(policy.type);
            change({ ...policy, rules: [...policy.rules, next] });
            logDataModels('app', 'governance_rule_added', { type: policy.type, ruleId: next.id });
          }}
        >
          <Plus size={14} />
          Add rule
        </button>
        {complexity === 'complex' ? (
          <div className="space-y-2">
            <label className={labelClass(isLight)}>Expression</label>
            <ExpressionEditor
              value={policy.advancedExpression}
              onChange={(advancedExpression) => change({ ...policy, advancedExpression })}
              assets={assetsWithColumns}
              isLight={isLight}
              {...workbenchProps}
            />
          </div>
        ) : null}
        {policy.type === 'SENSITIVE_DATA' && !readOnly ? (
          <div className="flex flex-wrap gap-2">
            <PresetButton isLight={isLight} label="Protect all PII" onClick={() => applyPreset(policy, onChange, 'SENSITIVE_DATA', ['PII'], 'full_string', { mode: 'roles', roles: ['analyst'] })} />
            <PresetButton isLight={isLight} label="Restrict payroll" onClick={() => applyPreset(policy, onChange, 'SENSITIVE_DATA', ['Restricted'], 'deny_asset', { mode: 'attributes', attribute: { key: 'department', operator: 'neq', value: 'HR' } })} />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className={`text-xs ${muted}`}>
        {policy.type === 'TIME_BASED'
          ? complexity === 'simple' ? 'Set when access applies for each audience.' : 'Add exceptions, patterns, and custom expressions.'
          : complexity === 'simple' ? 'Mask classified fields for everyone except a chosen audience.' : 'Add exceptions, patterns, and custom expressions.'}
      </p>
      {policy.rules.map((item) => {
        const accessAction = item.action.type === 'ACCESS' ? item.action : null;
        const maskAction = item.action.type === 'COLUMN' || item.action.type === 'SENSITIVE' ? item.action : null;
        return (
        <article key={item.id} className={`rounded-md border p-3 ${isLight ? 'border-[#E6E8EB] bg-[#F7F8FA]' : 'border-white/10 bg-white/[0.03]'}`}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className={`text-sm font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{summarizeRule(item, directoryUsers)}</p>
            {policy.rules.length > 1 ? <button type="button" disabled={readOnly} className={ghostBtn(isLight)} onClick={() => change({ ...policy, rules: policy.rules.filter((entry) => entry.id !== item.id) })}>Remove</button> : null}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <SubjectFields subject={complexity === 'simple' && item.subject.mode === 'expression' ? { ...item.subject, mode: 'roles' } : item.subject} onChange={(subject) => change(updateRule(policy, item.id, { subject }))} isLight={isLight} assets={scoped} />
            {maskAction ? (
              <MaskFields mask={maskAction.mask} onChange={(mask) => change(updateRule(policy, item.id, { action: { ...maskAction, mask } }))} isLight={isLight} />
            ) : accessAction ? (
              <div className="space-y-3">
                <div>
                  <label className={labelClass(isLight)}>They</label>
                  <select disabled={readOnly} className={fieldClass(isLight)} value={accessAction.effect} onChange={(event) => change(updateRule(policy, item.id, { action: { type: 'ACCESS', effect: event.target.value as 'allow' | 'deny', operations: accessAction.operations } }))}>
                    <option value="allow">can</option>
                    <option value="deny">cannot</option>
                  </select>
                </div>
                <div className={`divide-y rounded-md border ${isLight ? 'divide-[#E6E8EB] border-[#E6E8EB] bg-white' : 'divide-white/10 border-white/10 bg-black'}`}>
                  {OPERATIONS.map((operation) => {
                    const checked = accessAction.operations.includes(operation);
                    return (
                      <label key={operation} className="flex items-center gap-2 px-3 py-2 text-sm">
                        <input type="checkbox" disabled={readOnly} checked={checked} onChange={() => change(updateRule(policy, item.id, { action: { type: 'ACCESS', effect: accessAction.effect, operations: toggle(accessAction.operations, operation) as AccessOperation[] } }))} />
                        <span>{OPERATION_LABEL[operation]}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
          {complexity === 'complex' ? (
            <div className="mt-3">
              <label className={labelClass(isLight)}>Condition (optional)</label>
              <input disabled={readOnly} className={fieldClass(isLight)} value={item.expression ?? ''} onChange={(event) => change(updateRule(policy, item.id, { expression: event.target.value }))} placeholder="${user.region} = 'US'" />
            </div>
          ) : null}
        </article>
        );
      })}
      {complexity === 'complex' ? (
        <button type="button" disabled={readOnly} className={ghostBtn(isLight)} onClick={() => change({ ...policy, rules: [...policy.rules, defaultRule(policy.type)] })}>
          Add another audience
        </button>
      ) : null}
      {complexity === 'complex' ? (
        <div className="space-y-2">
          <label className={labelClass(isLight)}>Expression</label>
          <ExpressionEditor
            value={policy.advancedExpression}
            onChange={(advancedExpression) => change({ ...policy, advancedExpression })}
            assets={scoped}
            isLight={isLight}
            {...workbenchProps}
          />
        </div>
      ) : null}
    </div>
  );
}
